class PromoOfferPopup extends HTMLElement {
  #storageKey = 'ochaya-promo-offer-dismissed-v1';
  #dismissDurationMs = 7 * 24 * 60 * 60 * 1000;
  #delayMs = 1800;
  #dialog = null;
  #teaser = null;
  #openButton = null;
  #dismissButton = null;
  #closeButton = null;
  #bottomOffsetPx = 0;
  #boundOpen = this.#open.bind(this);
  #boundDismiss = this.#dismiss.bind(this);
  #boundClose = this.#close.bind(this);
  #boundDialogClick = this.#handleDialogClick.bind(this);
  #boundReposition = this.#positionTeaser.bind(this);

  connectedCallback() {
    this.#dialog = this.querySelector('[data-promo-dialog]');
    this.#teaser = this.querySelector('[data-promo-teaser]');
    this.#openButton = this.querySelector('[data-promo-open]');
    this.#dismissButton = this.querySelector('[data-promo-dismiss]');
    this.#closeButton = this.querySelector('[data-promo-close]');

    if (
      !(this.#dialog instanceof HTMLDialogElement) ||
      !(this.#teaser instanceof HTMLElement) ||
      !(this.#openButton instanceof HTMLElement) ||
      !(this.#dismissButton instanceof HTMLElement) ||
      !(this.#closeButton instanceof HTMLElement)
    ) {
      return;
    }

    const delay = Number.parseInt(this.dataset.delay || '', 10);
    if (!Number.isNaN(delay)) {
      this.#delayMs = delay;
    }

    const dismissDays = Number.parseInt(this.dataset.dismissDays || '', 10);
    if (!Number.isNaN(dismissDays) && dismissDays > 0) {
      this.#dismissDurationMs = dismissDays * 24 * 60 * 60 * 1000;
    }

    this.style.setProperty('--promo-bottom-offset', '0px');
    this.#openButton.addEventListener('click', this.#boundOpen);
    this.#dismissButton.addEventListener('click', this.#boundDismiss);
    this.#closeButton.addEventListener('click', this.#boundClose);
    this.#dialog.addEventListener('click', this.#boundDialogClick);
    this.#dialog.addEventListener('cancel', this.#boundClose);
    window.addEventListener('resize', this.#boundReposition);

    if (this.#isDesignMode()) {
      this.dataset.ready = 'true';
      window.requestAnimationFrame(this.#boundReposition);
      return;
    }

    if (this.#isDismissed()) {
      this.remove();
      return;
    }

    window.setTimeout(() => {
      if (!this.isConnected) return;
      this.dataset.ready = 'true';
      window.requestAnimationFrame(this.#boundReposition);
    }, this.#delayMs);
  }

  disconnectedCallback() {
    this.#openButton?.removeEventListener('click', this.#boundOpen);
    this.#dismissButton?.removeEventListener('click', this.#boundDismiss);
    this.#closeButton?.removeEventListener('click', this.#boundClose);
    this.#dialog?.removeEventListener('click', this.#boundDialogClick);
    this.#dialog?.removeEventListener('cancel', this.#boundClose);
    window.removeEventListener('resize', this.#boundReposition);
  }

  #isDesignMode() {
    return this.hasAttribute('data-design-mode') || document.documentElement.classList.contains('shopify-design-mode');
  }

  #readStoredDismissal() {
    try {
      return JSON.parse(localStorage.getItem(this.#storageKey) || 'null');
    } catch (_) {
      return null;
    }
  }

  #isDismissed() {
    const storedValue = this.#readStoredDismissal();
    if (!storedValue?.timestamp) return false;

    if (Date.now() - storedValue.timestamp <= this.#dismissDurationMs) {
      return true;
    }

    localStorage.removeItem(this.#storageKey);
    return false;
  }

  #storeDismissal() {
    try {
      localStorage.setItem(
        this.#storageKey,
        JSON.stringify({
          timestamp: Date.now(),
        })
      );
    } catch (_) {
      // no-op
    }
  }

  #open(event) {
    event.preventDefault();
    if (!(this.#dialog instanceof HTMLDialogElement)) return;
    if (this.#dialog.open) return;

    this.#dialog.showModal();
    this.dataset.modalOpen = 'true';
  }

  #close(event) {
    event?.preventDefault();
    if (!(this.#dialog instanceof HTMLDialogElement)) return;
    if (!this.#dialog.open) return;

    this.#dialog.close();
    delete this.dataset.modalOpen;
  }

  #dismiss(event) {
    event.preventDefault();
    this.#storeDismissal();
    this.#close();
    this.dataset.dismissing = 'true';

    window.setTimeout(() => {
      if (this.isConnected) {
        this.remove();
      }
    }, 220);
  }

  #handleDialogClick(event) {
    if (event.target === this.#dialog) {
      this.#close();
    }
  }

  #positionTeaser() {
    if (!this.dataset.ready || !(this.#teaser instanceof HTMLElement)) return;

    const button = this.#openButton;
    if (!(button instanceof HTMLElement)) return;

    this.#bottomOffsetPx = 0;
    this.style.setProperty('--promo-bottom-offset', '0px');

    const stepPx = 16;
    const maxOffsetPx = 220;

    const tryPosition = () => {
      if (!this.isConnected || !this.dataset.ready) return;

      const rect = button.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const hitTarget = document.elementFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);

      if (hitTarget instanceof Element && hitTarget.closest('promo-offer-popup') === this) {
        return;
      }

      if (this.#bottomOffsetPx >= maxOffsetPx) {
        return;
      }

      this.#bottomOffsetPx = Math.min(this.#bottomOffsetPx + stepPx, maxOffsetPx);
      this.style.setProperty('--promo-bottom-offset', `${this.#bottomOffsetPx}px`);
      window.requestAnimationFrame(tryPosition);
    };

    window.requestAnimationFrame(tryPosition);
  }
}

if (!customElements.get('promo-offer-popup')) {
  customElements.define('promo-offer-popup', PromoOfferPopup);
}
